import React, { useId } from 'react';
import { Box, Typography } from '@mui/material';

interface WordmarkProps {
  /** Height of the glyph mark in px. */
  size?: number;
  /** `dark` = placed on a dark surface (light text). */
  tone?: 'dark' | 'light';
  showText?: boolean;
  /** Render the mark with the brand gradient instead of a flat colour. */
  gradient?: boolean;
}

/**
 * Palbon mark + wordmark as inline SVG.
 *
 * Inline rather than the existing `logo192.png` because: it stays crisp at
 * 28px nav size (the raster is a 192px PWA icon), it recolours for the dark
 * hero and the light footer, and it costs no network request.
 */
const Wordmark: React.FC<WordmarkProps> = ({
  size = 32,
  tone = 'dark',
  showText = true,
  gradient = true,
}) => {
  // Unique per instance — duplicate SVG gradient ids across instances is a
  // classic silent rendering bug where every mark adopts the first one's fill.
  const rawId = useId();
  const gradientId = `palbon-mark-${rawId.replace(/:/g, '')}`;

  const flatColor = tone === 'dark' ? '#f8fafc' : '#0f172a';
  const fill = gradient ? `url(#${gradientId})` : flatColor;

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25 }}>
      <Box
        component="svg"
        viewBox="0 0 48 48"
        role="img"
        aria-label="Palbon ERP"
        sx={{ width: size, height: size, display: 'block', flexShrink: 0 }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#38bdf8" />
            <stop offset="50%" stopColor="#818cf8" />
            <stop offset="100%" stopColor="#a78bfa" />
          </linearGradient>
        </defs>

        {/* Rounded tile */}
        <rect x="0" y="0" width="48" height="48" rx="12" fill={fill} />

        {/* "P" counterform, knocked out of the tile */}
        <path
          d="M16 12h11a9 9 0 0 1 0 18h-5v6h-6V12zm6 5.5v7h4.5a3.5 3.5 0 0 0 0-7H22z"
          fill={tone === 'dark' ? '#05060f' : '#ffffff'}
        />
      </Box>

      {showText && (
        <Typography
          component="span"
          sx={{
            fontSize: size * 0.56,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: tone === 'dark' ? '#f8fafc' : '#0f172a',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          Palbon
          <Box component="span" sx={{ opacity: 0.55, ml: 0.75, fontWeight: 500 }}>
            ERP
          </Box>
        </Typography>
      )}
    </Box>
  );
};

export default Wordmark;
